import { useState, useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";

interface LivePresenceIndicatorProps {
  messages?: string[];
  intervalMs?: number;
  className?: string;
}

const DEFAULT_MESSAGES = [
  "Live scenes are active right now",
  "New people are joining Global Connect",
  "Rooftop vibes are trending",
  "Beach scene energy is up",
  "Café chats are starting",
  "Pop Balloon moments happening now",
  "Drop into a scene and say hi",
  "Virtual-first. No travel pressure.",
  "Respectful connections only",
  "Pick a country. Choose a vibe.",
  "Meet someone live in seconds",
];

export const LivePresenceIndicator = ({
  messages = DEFAULT_MESSAGES,
  intervalMs = 7000, // Default 7 seconds (between 6-8)
  className,
}: LivePresenceIndicatorProps) => {
  // Start at a random message on each page load
  const initialIndex = useMemo(() => {
    const validMessages = messages && messages.length > 0 ? messages : DEFAULT_MESSAGES;
    return Math.floor(Math.random() * validMessages.length);
  }, []);

  const [currentMessageIndex, setCurrentMessageIndex] = useState(initialIndex);
  const [isVisible, setIsVisible] = useState(true);

  // Ensure we always have valid messages
  const validMessages = useMemo(() => {
    return messages && messages.length > 0 ? messages : DEFAULT_MESSAGES;
  }, [messages]);

  // Rotate messages with gentle fade transition
  useEffect(() => {
    if (validMessages.length === 0) return;

    const interval = setInterval(() => {
      // Fade out
      setIsVisible(false);
      
      // After fade out, change message and fade in
      setTimeout(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % validMessages.length);
        setIsVisible(true);
      }, 300); // Wait for fade-out before changing message
    }, intervalMs);

    return () => clearInterval(interval);
  }, [validMessages.length, intervalMs]);

  // Always render dot + message together - guaranteed to have a message
  const currentMessage = validMessages[currentMessageIndex] || validMessages[0];

  return (
    <>
      <style>{`
        @keyframes pulse-gentle {
          0%, 100% { opacity: 0.75; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
      `}</style>
      <div
        className={cn(
          "flex items-center justify-center gap-1.5",
          "w-full max-w-md mx-auto px-4",
          "min-h-[28px] max-h-[56px]",
          className
        )}
      >
        {/* Live indicator dot - 6-8px, green, gently pulsing */}
        <div className="relative shrink-0 flex items-center justify-center w-2 h-2">
          {/* Pulsing outer ring */}
          <div 
            className="absolute w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500"
            style={{
              animation: 'pulse-gentle 2s ease-in-out infinite',
              opacity: 0.75,
            }}
          />
          {/* Solid inner dot */}
          <div className="absolute inset-0 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500" />
        </div>

        {/* Rotating message - responsive width, allows wrapping */}
        <div className="relative min-w-0 flex items-center justify-center">
          <p
            className={cn(
              "text-xs sm:text-sm font-normal text-gray-700",
              "text-center leading-relaxed", // Allow wrapping with proper line height
              "transition-all duration-300",
              "break-words", // Allow word breaking if needed
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-1"
            )}
            aria-live="polite"
            style={{ 
              color: '#374151',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}
          >
            {currentMessage}
          </p>
        </div>
      </div>
    </>
  );
};
